import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { theme, sunrisePrimitives } from '@vfit/shared/themes';
import { IInputsForSC } from './inputs.models';

export const inputsVariants = {
  inputOrTextarea: {
    animated: (props: IInputsForSC) => css`
      border-color: ${!props.error ? theme.colors.text.disabled : theme.colors.text.linkHover};
      border-radius: ${theme.radius.sm};

      &::placeholder {
        opacity: 0;
      }

      &:focus + span,
      &:not(:placeholder-shown) + span {
        font-size: ${theme.typography.fontSize.body3};
        line-height: ${theme.typography.lineHeight.body3};
        font-weight: ${theme.typography.fontWeight.regular};
        color: ${!props.disabled ? theme.colors.text.primary : theme.colors.text.secondary};
        transform: translate3d(0, -27px, 0);
      }
    `,
    'mva:animated': (props: IInputsForSC) => css`
      border-color: ${!props.error ? theme.colors.text.disabled : theme.colors.text.linkHover};
      border-radius: ${theme.radius.sm};
      ${props.error ? `box-shadow: 0 0 0 1px ${theme.colors.text.linkHover};` : ''}

      &::placeholder {
        opacity: 0;
      }

      &:focus {
        border-color: ${sunrisePrimitives.cyanLight};
        box-shadow: 0 0 0 1px ${sunrisePrimitives.cyanLight};
      }

      &:focus + span,
      &:not(:placeholder-shown) + span {
        font-size: ${theme.typography.fontSize.body3};
        line-height: ${theme.typography.lineHeight.body3};
        font-weight: ${theme.typography.fontWeight.regular};
        color: ${!props.disabled ? theme.colors.text.primary : theme.colors.text.secondary};
        transform: translate3d(0, -19px, 0);
      }
    `,
    'mva:static': (props: IInputsForSC) => css`
      border-color: ${!props.error
        ? theme.colors.brand.primaryDisabled
        : theme.colors.brand.primaryHover};
      border-radius: ${theme.radius.md};
      ${props.error ? `box-shadow: 0 0 0 1px ${theme.colors.text.linkHover};` : ''};

      &::placeholder {
        color: ${theme.colors.text.secondary};
        opacity: 1;
      }

      &:focus {
        border-color: ${sunrisePrimitives.cyanLight};
        box-shadow: 0 0 0 1px ${sunrisePrimitives.cyanLight};
      }
    `,
  },
};

const variants = {
  container: {
    animated: css`
      padding-top: 9px;
      padding-bottom: 24px;
    `,
    'mva:animated': css`
      padding-top: 9px;
      padding-bottom: 27px;
    `,
    'mva:static': css`
      padding-top: 36px;
      padding-bottom: 27px;
      padding-left: 2px;
      padding-right: 2px;
    `,
  },
  input: {
    animated: (props: IInputsForSC) => css`
      ${inputsVariants.inputOrTextarea.animated(props)}
      height: 60px;
      // padding: ${!props.svg ? '18px 20px' : '18px 56px 18px 20px'};
      padding: ${!props.svg ? '0px 20px' : '0 0 0 20px'};
    `,
    'mva:animated': (props: IInputsForSC) => css`
      ${inputsVariants.inputOrTextarea['mva:animated'](props)}
      height: 44px;
      // padding: ${!props.svg ? '9px 15px' : '9px 54px 9px 15px'};
      padding: ${!props.svg ? '9px 15px' : '0 0 0 15px'};
    `,
    'mva:static': (props: IInputsForSC) => css`
      ${inputsVariants.inputOrTextarea['mva:static'](props)}
      height: 44px;
      padding: ${!props.svg ? '9px 15px' : '9px 54px 9px 15px'};
    `,
  },
  labelPlaceholder: {
    animated: (props: IInputsForSC) => css`
      font-family: ${theme.typography.fontFamily.primary};
      font-weight: ${theme.typography.fontWeight.regular};
      border-radius: ${theme.radius.sm};
      color: ${theme.colors.text.secondary};
      left: 13px;
      padding: 0 ${theme.spacing.sm};
      top: ${theme.spacing.md};
    `,
    'mva:animated': (props: IInputsForSC) => css`
      font-size: ${theme.typography.fontSize.body1};
      line-height: ${theme.typography.lineHeight.body1};
      font-weight: ${theme.typography.fontWeight.regular};
      border-radius: ${theme.radius.sm};
      color: ${theme.colors.text.secondary};
      left: 12px;
      padding: '0 ${theme.spacing.xs}';
      top: ${theme.spacing.md};
    `,
    'mva:static': (props: IInputsForSC) => css`
      font-size: ${theme.typography.fontSize.body1};
      line-height: ${theme.typography.lineHeight.body1};
      font-weight: ${theme.typography.fontWeight.regular};
      color: ${!props.disabled ? theme.colors.text.primary : theme.colors.text.secondary};
      background-color: transparent;
      left: 0;
      padding: 2px 0;
      top: calc(-1 * ${theme.spacing.xl});
    `,
  },
};

export const input = (props: IInputsForSC) => css`
  font-size: ${theme.typography.fontSize.body1};
  line-height: ${theme.typography.lineHeight.body1};
  font-weight: ${theme.typography.fontWeight.regular};
  border-width: 1px;
  border-style: solid;
  box-sizing: border-box;
  color: ${!props.disabled ? theme.colors.text.primary : theme.colors.text.secondary};
  outline: 0; // TODO This is a bad practice
  transition: border-color 0.3s ease, box-shadow 0.3s ease;
  width: 100%;

  ::-ms-reveal,
  ::-ms-clear {
    display: none;
  }

  &::file-selector-button {
    display: none;
  }

  ${props.bg ? `background-color: ${props.bg}` : ''};
  ${props.variant ? variants.input[props.variant](props) : ''}
`;

export const checkbox = css`
  align-items: center;
  appearance: none;
  border: 1px solid ${theme.colors.text.disabled};
  border-radius: ${theme.radius.sm};
  display: inline-flex;
  flex-shrink: 0;
  height: 20px;
  justify-content: center;
  padding: 0;
  transition: background-color 0.3s ease;
  width: 20px;

  &::before {
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 12 9'%3E%3Cpath stroke='%23fff' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M11 1 4.158 8 1 4.77'/%3E%3C/svg%3E");
    content: '';
    display: block;
    height: 9px;
    width: 12px;
  }

  &:checked {
    background-color: ${sunrisePrimitives.cyanDark};
    border-color: ${sunrisePrimitives.cyanDark};
  }
`;

export const radio = css`
  align-items: center;
  appearance: none;
  border: 1px solid ${theme.colors.brand.primaryDisabled};
  border-radius: 50%;
  display: inline-flex;
  flex-shrink: 0;
  height: 20px;
  justify-content: center;
  padding: 0;
  transition: background-color 0.3s ease;
  width: 20px;

  &::before {
    border-color: ${sunrisePrimitives.cyanDark};
    border-radius: 50%;
    content: '';
    display: block;
    height: 12px;
    transition: background-color 0.3s ease;
    width: 12px;
  }

  &:checked::before {
    background-color: ${sunrisePrimitives.cyanDark};
    border-color: ${sunrisePrimitives.cyanDark};
  }

  &:checked {
    border-color: ${sunrisePrimitives.cyanDark};
  }
`;

export const container = (props: IInputsForSC) => css`
  ${props.hidden ? 'visibility: hidden;' : ''}
  position: relative;
  ${props.variant ? variants.container[props.variant] : ''}
`;

export const Container = styled.div<IInputsForSC>`
  ${container}
`;

export const Label = styled.label`
  display: block;
  position: relative;
  width: 100%;
  z-index: 1;
`;

export const LabelPlaceholder = styled.span<IInputsForSC>`
  pointer-events: none;
  position: absolute;
  transition: all 0.3s ease;
  ${({ bg }) => (bg ? `background-color: ${bg};` : '')};
  ${(props) => (props.variant ? variants.labelPlaceholder[props.variant](props) : '')}
`;

